import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { Sale, SaleItem } from '@/lib/types';

const PAGE_SIZE = 20;

export function useSales(branchId?: string | null) {
  const [sales, setSales] = useState<Sale[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pageRef = useRef(0);

  const runQuery = useCallback(
    async (reset: boolean) => {
      if (reset) {
        setLoading(true);
        pageRef.current = 0;
      } else {
        setLoadingMore(true);
      }
      setError(null);
      const from = pageRef.current * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      try {
        let query = supabase
          .from('sales')
          .select('*, branch:branches(name)', { count: 'exact' })
          .range(from, to)
          .order('created_at', { ascending: false });
        if (branchId) {
          query = query.eq('branch_id', branchId);
        }
        const { data, count, error: e } = await query;
        if (e) throw e;
        setTotal(count ?? 0);
        setSales((prev) => (reset ? ((data as Sale[]) ?? []) : [...prev, ...((data as Sale[]) ?? [])]));
        pageRef.current += 1;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'No se pudieron cargar las ventas');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [branchId]
  );

  useEffect(() => {
    runQuery(true);
  }, [runQuery]);

  const loadMore = useCallback(() => runQuery(false), [runQuery]);
  const hasMore = sales.length < total;

  return { sales, total, loading, loadingMore, error, hasMore, loadMore, reload: () => runQuery(true) };
}

export function useSale(id: string | undefined) {
  const [sale, setSale] = useState<Sale | null>(null);
  const [items, setItems] = useState<SaleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { data: saleData, error: saleError } = await supabase
        .from('sales')
        .select('*, branch:branches(name, legal_name, rnc, logo_url, address, phone, whatsapp, email, description)')
        .eq('id', id)
        .maybeSingle();
      if (saleError) throw saleError;

      const { data: itemData, error: itemError } = await supabase
        .from('sale_items')
        .select('*')
        .eq('sale_id', id)
        .order('created_at', { ascending: true });
      if (itemError) throw itemError;

      setSale((saleData as Sale) ?? null);
      setItems((itemData as SaleItem[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar la factura');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  return { sale, items, loading, error, reload: load };
}